// WAAP Bot Management — account-level bot strategies (vendor "bot_strategy").
//
// A strategy is created once and applied to one or more sites ("Apply to"). It is
// backed by the PRIVATE portal (waap2), NOT the Open-API SDK. The eagle_waf
// backend (modules/service/eagle_waf/bot) proxies the portal and — like the
// account-level `strategies` reads — mounts under the per-domain route, where the
// `:id` domain only resolves the Conversant customer session (the data is
// account-wide). So the hooks take an ANCHOR domain UUID (any account website):
//
//   GET    /waap/domains/:id/bot-strategies        → portal GET    /bot/bot_strategies?type=1
//   POST   /waap/domains/:id/bot-strategies        → portal POST   /bot/bot_strategy
//   GET    /waap/domains/:id/bot-strategies/:sid   → list + filter (no captured get-one)
//   PUT    /waap/domains/:id/bot-strategies/:sid   → portal PUT    /bot/bot_strategy (id in body)
//   DELETE /waap/domains/:id/bot-strategies/:sid   → portal DELETE /bot/bot_strategy/:sid
//   GET    /waap/domains/:id/bot-enabled-sites     → portal GET    /sites/enabled
//
// The clean DTO below is what the FE sends; the BE assembles the portal envelope
// (top-level {id, customer_id, name, description, type:1, sites} + nested `config`,
// duplicating name→config.name and description→config.comment).

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';

// ---- DTOs ----------------------------------------------------------------

/** One Browser-Interrogation condition (analyse a browser/device signal). */
export interface BotBrowserInterrogation {
  /** Always 1 (vendor "enable" flag on the row). */
  enable: number;
  /** Vendor match parameter, from BOT_METRICS (e.g. "MouseClickOrNot"). */
  matchParameter: string;
  /** Match operator, from BOT_METRIC_OPERATORS (e.g. "Equal"). */
  matchOperator: string;
  /** Expected value: "true" (Yes) | "false" (No). */
  matchValue: string;
}

/** One Request-Whitelist rule (requests that bypass bot detection). */
export interface BotRequestWhitelistRule {
  /** Metric key, from BOT_WHITELIST_METRICS ("path" | "extension" | "ua" | "country"). */
  key: string;
  /** Operation, from the metric's operations (e.g. "equal" | "include" | "contain"). */
  operation: string;
  /** Restrict to GET requests only. Defaults false. */
  get_only: boolean;
  /** Single string for text metrics (path/ua); string[] for list metrics
   *  (extension/country — the portal serialises these as an array). */
  value: string | string[];
}

/** A bot strategy (create/update body + get-one). `id` absent when creating. */
export interface BotStrategy {
  id?: number;
  name: string;
  description: string;
  /** Dynamic Token Protection is always on; preset fixed to "high" for now. */
  protection_level: string;
  silence_challenge_enabled: boolean;
  /** "block" | "captcha" — only meaningful when silence_challenge_enabled. */
  silence_challenge_action: string;
  crawl_control_enabled: boolean;
  /** Selected crawler IDs (see BOT_CRAWLERS). */
  crawl_control: number[];
  ajax_enabled: boolean;
  anti_debugging_enabled: boolean;
  strict_mode: boolean;
  browser_interrogation_list: BotBrowserInterrogation[];
  request_whitelist: BotRequestWhitelistRule[];
  /** Site IDs this strategy applies to (BotEnabledSite.id). */
  sites: number[];
}

/** A row in the Bot Management list. */
export interface BotStrategyListItem {
  id: number;
  name: string;
  description: string;
  /** Site IDs the strategy applies to (resolved to names via enabled-sites). */
  sites: number[];
}

/** An "enabled" site for the Apply-to picker (portal /sites/enabled). */
export interface BotEnabledSite {
  id: number;
  domain: string;
  domain_id: number;
  protection: boolean;
  protectionMode: string;
  status: number;
  customer_id: number;
}

/** A factory for a blank strategy (all crawlers on, sensible defaults). */
export function emptyBotStrategy(allCrawlerIds: number[]): BotStrategy {
  return {
    name: '',
    description: '',
    protection_level: 'high',
    silence_challenge_enabled: false,
    silence_challenge_action: 'block',
    crawl_control_enabled: false,
    crawl_control: allCrawlerIds,
    ajax_enabled: false,
    anti_debugging_enabled: false,
    strict_mode: false,
    browser_interrogation_list: [],
    request_whitelist: [],
    sites: [],
  };
}

// ---- Catalogs ------------------------------------------------------------

/** Browser-Interrogation metrics (label ↔ vendor matchParameter).
 *  NOTE: only "MouseClickOrNot" is confirmed from a captured payload — the rest
 *  follow the same `…OrNot` naming and MUST be verified by the lead against the
 *  portal before BE go-live. */
export const BOT_METRICS: { value: string; label: string }[] = [
  { value: 'MouseClickOrNot', label: 'Mouse Click' }, // confirmed
  { value: 'MouseMoveOrNot', label: 'Mouse Movement' }, // TODO verify
  { value: 'KeyboardOrNot', label: 'Keyboard Activity' }, // TODO verify
  { value: 'TouchMoveOrNot', label: 'Touchpad Movement' }, // TODO verify
  { value: 'TouchOrNot', label: 'Touchpad Activity' }, // TODO verify
  { value: 'BrowserExtensionOrNot', label: 'Browser Extension' }, // TODO verify
  { value: 'SystemLanguageOrNot', label: 'Device System Language' }, // TODO verify
];

export const BOT_METRIC_OPERATORS: { value: string; label: string }[] = [
  { value: 'Equal', label: 'Equal to' },
];

export const BOT_METRIC_VALUES: { value: string; label: string }[] = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

export const BOT_SILENCE_ACTIONS: { value: string; label: string }[] = [
  { value: 'block', label: 'Block' },
  { value: 'captcha', label: 'Captcha' },
];

/** How a Request-Whitelist metric's Value is entered/serialised:
 *  'text' → single string · 'list' → free-form string[] · 'country' → ISO-code string[]. */
export type BotWhitelistValueKind = 'text' | 'list' | 'country';

/** One Request-Whitelist metric with its metric-dependent operations + value kind,
 *  mirroring the portal's Bot Management Request Whitelist. All operation wire
 *  values verified against the live vendor (create accepted): path equal/not_equal/
 *  begins_with/ends_with/regex · extension include/exclude · ua contain/not_contain
 *  · country include/exclude. Value is a plain string for path/ua and a string[]
 *  for extension/country (the portal serialises the latter as an array). */
export interface BotWhitelistMetric {
  key: string;
  label: string;
  valueKind: BotWhitelistValueKind;
  operations: { value: string; label: string }[];
}

export const BOT_WHITELIST_METRICS: BotWhitelistMetric[] = [
  {
    key: 'path',
    label: 'Path',
    valueKind: 'text',
    operations: [
      { value: 'equal', label: 'Equal to' },
      { value: 'not_equal', label: 'Not equal to' },
      { value: 'begins_with', label: 'Begins with' },
      { value: 'ends_with', label: 'Ends with' },
      { value: 'regex', label: 'Regex' },
    ],
  },
  {
    key: 'extension',
    label: 'Extension',
    valueKind: 'list',
    operations: [
      { value: 'include', label: 'Include' },
      { value: 'exclude', label: 'Exclude' },
    ],
  },
  {
    key: 'ua',
    label: 'User Agent',
    valueKind: 'text',
    operations: [
      { value: 'contain', label: 'Contains' },
      { value: 'not_contain', label: 'Does not contain' },
    ],
  },
  {
    key: 'country',
    label: 'Country ISO Code',
    valueKind: 'country',
    operations: [
      { value: 'include', label: 'Include' },
      { value: 'exclude', label: 'Exclude' },
    ],
  },
];

/** Lookup a whitelist metric by key. */
export const botWhitelistMetric = (key: string): BotWhitelistMetric | undefined =>
  BOT_WHITELIST_METRICS.find((m) => m.key === key);

/** Display label for a whitelist metric key. */
export const botWhitelistMetricLabel = (key: string): string => botWhitelistMetric(key)?.label ?? key;

/** Display label for a whitelist operation within its metric. */
export const botWhitelistOpLabel = (key: string, op: string): string =>
  botWhitelistMetric(key)?.operations.find((o) => o.value === op)?.label ?? op;

/** Crawl-Control crawler catalog (ID ↔ name). Hardcoded from the SwiftFederation
 *  portal (it hardcodes this list too — there is no GET). The vendor groups IDs
 *  by category prefix: 1000x social/ad · 101xx RSS readers · 102xx link crawlers
 *  · 103xx headless browsers · 104xx search engines · 105xx monitoring · 106xx
 *  scrapers · 107xx misc bots. Order matches the captured crawl_control[] payload. */
export interface BotCrawler {
  id: number;
  name: string;
}

export const BOT_CRAWLERS: BotCrawler[] = [
  // 1000x — social / advertising
  { id: 10000, name: 'Facebook crawler for link preview' },
  { id: 10001, name: 'Twitter crawler for link preview' },
  { id: 10002, name: 'Google Adsense Bot to serve ad' },
  { id: 10003, name: 'Google Ads Bot to evaluate publisher' },
  // 101xx — RSS / feed readers
  { id: 10100, name: 'Bloglines news aggregator to read RSS feed' },
  { id: 10101, name: 'Newsgator content aggregator to read RSS feed' },
  { id: 10102, name: 'Netnewswire RSS feed reader for macOS and iOS' },
  { id: 10103, name: 'Feedfetcher RSS feed reader' },
  // 102xx — link crawlers
  { id: 10200, name: 'Link integrity checker' },
  { id: 10201, name: 'LinkWalker crawler for SEO and data aggregation' },
  { id: 10202, name: 'Backlink crawler (Zoombot, SepLinkBot, RankActiveLinkBot)' },
  { id: 10203, name: 'Findlinks lightweight crawler for language research purpose' },
  { id: 10204, name: 'Backlinkcrawler SEO backlink crawler' },
  { id: 10205, name: 'Xenu link sleuth, windows tool to crawl websites' },
  // 103xx — headless browsers
  { id: 10300, name: 'Affiliate Puppeteer Headless Browser' },
  { id: 10301, name: 'Phantom JS Headless Browser' },
  { id: 10302, name: 'Screenshot Tool' },
  // 104xx — search engine crawlers
  { id: 10400, name: 'Semrush Web Crawler' },
  { id: 10401, name: 'DuckDuckGo Search Engine Web Crawler' },
  { id: 10402, name: 'Baidu Search Engine Web Crawler' },
  { id: 10403, name: 'WebMeUp BLEXBot Web Crawler' },
  { id: 10404, name: 'Soso Search Engine Web Crawler' },
  { id: 10405, name: 'Bing Web Crawler (bingbot and adidxbot)' },
  { id: 10406, name: 'Yandex Search Engine Web Crawler' },
  { id: 10407, name: 'Sogou Search Engine Web Crawler' },
  { id: 10408, name: 'Google Search Engine Bot' },
  { id: 10409, name: 'Majestic-12 Ltd. Web Crawler (search for backlink)' },
  { id: 10410, name: 'Ahrefs Web Crawler' },
  // 105xx — monitoring tools
  { id: 10500, name: 'General Monitoring Tool' },
  { id: 10501, name: 'Pingdom Monitoring Tool' },
  { id: 10502, name: 'StatusCake Monitoring Tool' },
  { id: 10503, name: 'General Monitoring Tool 2' },
  // 106xx — scrapers
  { id: 10600, name: 'Web Scraper' },
  { id: 10601, name: 'General Web Scraper' },
  { id: 10602, name: 'General Web Scraper 2' },
  { id: 10603, name: 'General Web Scraper 3' },
  { id: 10604, name: 'semantifire1 by SETU Software Systems (P) Ltd' },
  // 107xx — misc bots
  { id: 10700, name: 'Web Bot' },
  { id: 10701, name: 'General Web Crawler Tool' },
  { id: 10702, name: 'Website Sitemap Generator Crawler' },
  { id: 10703, name: 'siteanalyzerbot Website Crawler' },
  { id: 10704, name: 'Webpage Analyser Bot' },
];

export const ALL_CRAWLER_IDS: number[] = BOT_CRAWLERS.map((c) => c.id);

// ---- Paths + query keys --------------------------------------------------
//
// Bot Management is account-level, but the eagle_waf backend (like the
// `strategies` reads) mounts it under the per-domain route — the `:id` domain
// only resolves the Conversant customer session; the strategies/sites are
// account-wide. So callers pass an ANCHOR domain UUID (any website in the
// account, e.g. websites[0]); all calls return the whole account's data.

function strategiesPath(domainId: string): string {
  return `/waap/domains/${domainId}/bot-strategies`;
}
function enabledSitesPath(domainId: string): string {
  return `/waap/domains/${domainId}/bot-enabled-sites`;
}

export const botKeys = {
  all: ['waap', 'bot'] as const,
  strategies: (domainId: string) => ['waap', 'bot', domainId, 'strategies'] as const,
  strategy: (domainId: string, id: string | number) =>
    ['waap', 'bot', domainId, 'strategies', String(id)] as const,
  enabledSites: (domainId: string) => ['waap', 'bot', domainId, 'enabled-sites'] as const,
};

// ---- Hooks ---------------------------------------------------------------

/** List the account's bot strategies (anchored on any account domain UUID). */
export function useBotStrategies(domainId: string | undefined) {
  return useQuery({
    queryKey: botKeys.strategies(domainId ?? ''),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<BotStrategyListItem[]> => {
      const res = await serviceApiV3.get<BotStrategyListItem[] | { data: BotStrategyListItem[] }>(
        strategiesPath(domainId as string),
      );
      // Tolerate both bare-array and {data:[...]} shapes.
      const body = res.data as BotStrategyListItem[] | { data?: BotStrategyListItem[] };
      return Array.isArray(body) ? body : body?.data ?? [];
    },
  });
}

/** Fetch one bot strategy (for the edit form). */
export function useBotStrategy(domainId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: botKeys.strategy(domainId ?? '', id ?? ''),
    enabled: !!domainId && !!id,
    retry: false,
    queryFn: async (): Promise<BotStrategy> => {
      const res = await serviceApiV3.get<BotStrategy | { data: BotStrategy }>(
        `${strategiesPath(domainId as string)}/${id}`,
      );
      const body = res.data as BotStrategy | { data?: BotStrategy };
      return (body as { data?: BotStrategy })?.data ?? (body as BotStrategy);
    },
  });
}

/** Enabled sites for the Apply-to picker (anchored on any account domain UUID). */
export function useBotEnabledSites(domainId: string | undefined) {
  return useQuery({
    queryKey: botKeys.enabledSites(domainId ?? ''),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<BotEnabledSite[]> => {
      const res = await serviceApiV3.get<BotEnabledSite[] | { data: BotEnabledSite[] }>(
        enabledSitesPath(domainId as string),
      );
      const body = res.data as BotEnabledSite[] | { data?: BotEnabledSite[] };
      return Array.isArray(body) ? body : body?.data ?? [];
    },
  });
}

function invalidateStrategies(qc: ReturnType<typeof useQueryClient>, domainId: string) {
  qc.invalidateQueries({ queryKey: botKeys.strategies(domainId) });
}

/** Create a bot strategy. */
export function useCreateBotStrategy(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, BotStrategy>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BotStrategy) => {
      const res = await serviceApiV3.post(strategiesPath(domainId), body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      invalidateStrategies(qc, domainId);
      options?.onSuccess?.(...args);
    },
  });
}

/** Update a bot strategy. */
export function useUpdateBotStrategy(
  domainId: string,
  id: number | string,
  options?: UseMutationOptions<unknown, unknown, BotStrategy>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BotStrategy) => {
      const res = await serviceApiV3.put(`${strategiesPath(domainId)}/${id}`, body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      invalidateStrategies(qc, domainId);
      qc.invalidateQueries({ queryKey: botKeys.strategy(domainId, id) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete a bot strategy. */
export function useDeleteBotStrategy(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, number | string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await serviceApiV3.delete(`${strategiesPath(domainId)}/${id}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      invalidateStrategies(qc, domainId);
      options?.onSuccess?.(...args);
    },
  });
}

// ---- Service-scoped twins (account-level, work with zero websites) ----------
// Mirror the domain-anchored hooks above but build /services/${serviceId}/waap/...
// so Bot Management renders without a carrier domain anchor.

function serviceStrategiesPath(serviceId: string): string {
  return `/services/${serviceId}/waap/bot-strategies`;
}
function serviceEnabledSitesPath(serviceId: string): string {
  return `/services/${serviceId}/waap/bot-enabled-sites`;
}

export const botServiceKeys = {
  strategies: (serviceId: string) => ['waap', 'bot', 'service', serviceId, 'strategies'] as const,
  strategy: (serviceId: string, id: string | number) =>
    ['waap', 'bot', 'service', serviceId, 'strategies', String(id)] as const,
  enabledSites: (serviceId: string) =>
    ['waap', 'bot', 'service', serviceId, 'enabled-sites'] as const,
};

/** List the account's bot strategies, resolved from the service (no domain anchor). */
export function useServiceBotStrategies(serviceId: string | undefined) {
  return useQuery({
    queryKey: botServiceKeys.strategies(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<BotStrategyListItem[]> => {
      const res = await serviceApiV3.get<BotStrategyListItem[] | { data: BotStrategyListItem[] }>(
        serviceStrategiesPath(serviceId as string),
      );
      const body = res.data as BotStrategyListItem[] | { data?: BotStrategyListItem[] };
      return Array.isArray(body) ? body : body?.data ?? [];
    },
  });
}

/** Fetch one bot strategy, service-scoped. */
export function useServiceBotStrategy(serviceId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: botServiceKeys.strategy(serviceId ?? '', id ?? ''),
    enabled: !!serviceId && !!id,
    retry: false,
    queryFn: async (): Promise<BotStrategy> => {
      const res = await serviceApiV3.get<BotStrategy | { data: BotStrategy }>(
        `${serviceStrategiesPath(serviceId as string)}/${id}`,
      );
      const body = res.data as BotStrategy | { data?: BotStrategy };
      return (body as { data?: BotStrategy })?.data ?? (body as BotStrategy);
    },
  });
}

/** Enabled sites for the Apply-to picker, service-scoped. */
export function useServiceBotEnabledSites(serviceId: string | undefined) {
  return useQuery({
    queryKey: botServiceKeys.enabledSites(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<BotEnabledSite[]> => {
      const res = await serviceApiV3.get<BotEnabledSite[] | { data: BotEnabledSite[] }>(
        serviceEnabledSitesPath(serviceId as string),
      );
      const body = res.data as BotEnabledSite[] | { data?: BotEnabledSite[] };
      return Array.isArray(body) ? body : body?.data ?? [];
    },
  });
}

function invalidateServiceBotStrategies(
  qc: ReturnType<typeof useQueryClient>,
  serviceId: string,
) {
  qc.invalidateQueries({ queryKey: botServiceKeys.strategies(serviceId) });
}

/** Create a bot strategy, service-scoped. */
export function useCreateServiceBotStrategy(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, BotStrategy>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BotStrategy) => {
      const res = await serviceApiV3.post(serviceStrategiesPath(serviceId), body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      invalidateServiceBotStrategies(qc, serviceId);
      options?.onSuccess?.(...args);
    },
  });
}

/** Update a bot strategy, service-scoped. */
export function useUpdateServiceBotStrategy(
  serviceId: string,
  id: number | string,
  options?: UseMutationOptions<unknown, unknown, BotStrategy>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BotStrategy) => {
      const res = await serviceApiV3.put(`${serviceStrategiesPath(serviceId)}/${id}`, body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      invalidateServiceBotStrategies(qc, serviceId);
      qc.invalidateQueries({ queryKey: botServiceKeys.strategy(serviceId, id) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete a bot strategy, service-scoped. */
export function useDeleteServiceBotStrategy(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, number | string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await serviceApiV3.delete(`${serviceStrategiesPath(serviceId)}/${id}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      invalidateServiceBotStrategies(qc, serviceId);
      options?.onSuccess?.(...args);
    },
  });
}
